/**
 * Strict parsing for the inline planning inputs.
 *
 * A blank field means "clear" (only an explicit clear, never a silent one); a
 * well-formed number is a value; anything else is an error that stops the save
 * and leaves the stored value untouched. Kept pure so it can be unit-tested and
 * shared by the plan cells, the SKU planning form, and the settings panel.
 */

export type ParsedField<T> =
  { kind: 'value'; value: T } | { kind: 'clear' } | { kind: 'error'; message: string };

/**
 * An integer part with commas allowed ONLY as thousands separators in groups of
 * three: `1234` or `1,234` or `1,234,567`, but never `12,50` or `1,2,3`.
 */
const GROUPED_INT = String.raw`(?:\d{1,3}(?:,\d{3})+|\d+)`;

/** Parses a grouped integer string to a number, or null when malformed. */
function groupedInt(raw: string): number | null {
  const match = new RegExp(`^${GROUPED_INT}$`).exec(raw.trim());
  return match ? Number.parseInt(match[0].replace(/,/g, ''), 10) : null;
}

/**
 * A whole count: digits with optional thousands separators and surrounding
 * spaces. Empty clears; a malformed number (letters, stray commas, a decimal)
 * or one below `min` is an error — never silently coerced.
 */
export function parseCount(raw: string, { min = 0 }: { min?: number } = {}): ParsedField<number> {
  if (raw.trim() === '') return { kind: 'clear' };
  const value = groupedInt(raw);
  if (value === null) return { kind: 'error', message: 'Enter a whole number.' };
  if (value < min) return { kind: 'error', message: `Must be at least ${min}.` };
  return { kind: 'value', value };
}

/**
 * A dollar amount: an optional `$`, an integer part (commas only as thousands
 * separators), and at most two decimals ("12.5", "12.50", "$12.50",
 * "1,234.50"). Empty clears; more than two decimals, ambiguous commas
 * ("12,50", "1,2,3"), or non-numeric input is an error. Returns a normalized
 * `numeric`-friendly string (no `$`, no commas).
 */
export function parseMoney(raw: string): ParsedField<string> {
  if (raw.trim() === '') return { kind: 'clear' };
  const cleaned = raw.trim().replace(/^\$\s*/, '');
  const match = new RegExp(`^(${GROUPED_INT})(?:\\.(\\d{1,2}))?$`).exec(cleaned);
  if (!match) return { kind: 'error', message: 'Enter a dollar amount like 12.50.' };
  const int = (match[1] as string).replace(/,/g, '');
  return { kind: 'value', value: match[2] ? `${int}.${match[2]}` : int };
}

/**
 * A required settings integer within bounds. Unlike the fields above, a blank
 * is an error (never a silent 0), so a settings save can never zero a field.
 */
export function parseSettingInt(
  raw: string,
  { min, max }: { min: number; max: number },
): ParsedField<number> {
  if (raw.trim() === '') return { kind: 'error', message: 'This field is required.' };
  const value = groupedInt(raw);
  if (value === null) return { kind: 'error', message: 'Enter a whole number.' };
  if (value < min || value > max)
    return { kind: 'error', message: `Must be between ${min} and ${max}.` };
  return { kind: 'value', value };
}
